import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, ArrowRight, CheckCircle2, Phone } from "lucide-react";
import { SERVICES, WHY_US, FAQS, COMPANY, ASSETS } from "../mock";
import { SectionHeading, FAQAccordion } from "../components/common";
import { Button } from "../components/ui/button";
import useScrollReveal from "../hooks/useScrollReveal";
import useSEO from "../hooks/useSEO";

function PageHero({ crumb, title, subtitle }) {
  return (
    <section className="relative pt-24 pb-10 md:pt-40 md:pb-24 bg-brand-navy text-white overflow-hidden">
      <img src={ASSETS.pool3} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-25" />
      <div className="absolute inset-0 bg-gradient-to-br from-brand-navydark/90 via-brand-navy/85 to-brand-ocean/50" />
      <div className="container-x relative">
        <nav className="flex items-center gap-2 text-sm text-white/60 mb-5" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-brand-gold2">{crumb}</span>
        </nav>
        <h1 className="text-3xl md:text-5xl font-heading font-extrabold max-w-3xl">{title}</h1>
        <p className="mt-4 text-white/80 text-lg max-w-2xl">{subtitle}</p>
      </div>
    </section>
  );
}

export default function Services() {
  const navigate = useNavigate();
  useScrollReveal();
  useSEO({
    title: "Pool Cages, Patio Screens & Enclosure Services | Miller & Miller Services",
    description: "Explore premium pool enclosures, patio & porch screens, rescreening, carports & lanai/elite enclosures in Melbourne, FL. Licensed & insured with free estimates.",
    keywords: ["screen enclosure services", "pool enclosure contractor", "patio screen enclosure", "rescreening", "carport installation Melbourne FL"],
    path: "/services",
  });

  return (
    <div>
      <PageHero
        crumb="Services"
        title="Long-Lasting Outdoor Solutions For You"
        subtitle="Premium custom enclosures, storm protection, and outdoor upgrades engineered for comfort, security, and Florida's climate."
      />

      {/* Alternating detailed services */}
      <section className="section-y bg-white">
        <div className="container-x space-y-16 md:space-y-24">
          {SERVICES.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <div key={s.id} id={s.id} className="grid lg:grid-cols-2 gap-10 items-center scroll-mt-28">
                <div className={`reveal ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative rounded-3xl overflow-hidden shadow-card">
                    <img src={s.image} alt={s.title} loading="lazy" className="w-full h-[360px] object-cover" />
                    {s.tag && (
                      <span className="absolute top-4 left-4 bg-brand-gold text-brand-navy text-xs font-heading font-extrabold uppercase tracking-wide px-3 py-1.5 rounded-full shadow">
                        {s.tag}
                      </span>
                    )}
                  </div>
                </div>
                <div className={`reveal ${reverse ? "lg:order-1" : ""}`}>
                  <span className="text-xs font-heading font-bold uppercase tracking-[0.18em] text-brand-ocean">
                    0{i + 1} · Miller & Miller
                  </span>
                  <h2 className="mt-3 text-2xl md:text-3xl font-heading font-extrabold text-brand-navy">{s.title}</h2>
                  <p className="mt-4 text-brand-slate leading-relaxed">{s.long}</p>
                  <ul className="mt-5 grid sm:grid-cols-2 gap-3">
                    {s.features.map((f) => (
                      <li key={f} className="flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                        <CheckCircle2 className="h-5 w-5 text-brand-ocean shrink-0" /> {f}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-7 flex flex-wrap gap-3">
                    <Button onClick={() => navigate("/estimate")} className="h-12 px-7 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-bold shadow-gold">
                      Get My Free Quote
                    </Button>
                    <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-6 rounded-md ring-1 ring-brand-slate/25 hover:ring-brand-ocean text-brand-navy font-heading font-bold transition-all">
                      <Phone className="h-4 w-4" /> Call Now
                    </a>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Guarantees */}
      <section className="section-y bg-brand-mist/40">
        <div className="container-x">
          <SectionHeading center eyebrow="Why Trust Us" title="Our Promise To You" />
          <div className="mt-8 md:mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {WHY_US.map((w, i) => (
              <div key={i} className="reveal rounded-2xl bg-white p-7 shadow-card ring-1 ring-brand-mist" style={{ transitionDelay: `${i * 60}ms` }}>
                <h3 className="text-lg font-heading font-extrabold text-brand-navy">{w.title}</h3>
                <p className="mt-2 text-sm text-brand-slate leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-y bg-white">
        <div className="container-x max-w-3xl">
          <SectionHeading center eyebrow="Got Questions?" title="Frequently Asked Questions" />
          <div className="mt-10">
            <FAQAccordion faqs={FAQS} />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-r from-brand-ocean to-brand-sky">
        <div className="container-x py-14 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-extrabold text-white reveal">
            Ready to improve your outdoor living space?
          </h2>
          <p className="text-white/90 mt-3 reveal">Let's begin with a fast, free, no-obligation estimate.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3 reveal">
            <Button onClick={() => navigate("/estimate")} className="h-13 px-8 h-12 bg-brand-gold hover:bg-brand-gold2 text-brand-navy font-heading font-extrabold shadow-gold">
              Get My Free Quote <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
            <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 h-12 px-7 rounded-md bg-brand-navy/30 hover:bg-brand-navy/50 text-white font-heading font-bold ring-1 ring-white/30 transition-colors">
              <Phone className="h-4 w-4" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
